import {
	Box,
	Button,
	Text,
	Heading,
	Flex,
	Icon,
	Checkbox,
	Tag,
	TagLabel,
	TagCloseButton,
	Menu,
	MenuButton,
	MenuList,
	MenuItem,
	Portal,
	Badge,
	Tooltip,
	HStack,
	Image,
} from '@chakra-ui/react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useState } from 'react';
import YaxleLogo from '../../assets/yaxleLogoBlue.png';
import { brandCopy } from '../../constants/brand';
import { StepFooter } from './StepFooter';

export interface PtSupplier {
	id: number;
	name: string;
	partTypes: string[];
}

export interface OpsSupplier {
	dealerId: number;
	name: string;
	partTypes: string[];
}

/**
 * Onboarding never *pulls* CCC vendors, but existing suppliers may already carry
 * a cccId from a prior CCC-side workflow. We read it for the "already in DB" summary
 * so the reviewer can see full cross-source linkage, but this UI never edits it.
 */
export interface ExistingSupplier {
	_id: string;
	title: string;
	partstraderId: number[];
	opsId: number[];
	cccId?: number[];
	partTypes?: string[];
}

export interface SupplierMapping {
	ptSupplierId?: number;
	opsDealerIds?: number[];
	existingId?: string;
}

export interface SupplierRowMapping {
	enabled: boolean;
	ptIds: number[];
	opsDealerIds: number[];
	existingId?: string;
	existingHasOpsId?: boolean;
}

interface SupplierMappingUIProps {
	ptSuppliers: PtSupplier[];
	opsSuppliers: OpsSupplier[];
	existingSuppliers?: ExistingSupplier[];
	suggestedMappings?: SupplierMapping[];
	onConfirm: (mappings: SupplierMapping[]) => void;
	isConfirmLoading?: boolean;
	rowMappings: Record<string, SupplierRowMapping>;
	onToggleRow: (rowKey: string) => void;
	onAddPt: (rowKey: string, ptId: number) => void;
	onRemovePt: (rowKey: string, ptId: number) => void;
	onAddOps: (rowKey: string, dealerId: number) => void;
	onRemoveOps: (rowKey: string, dealerId: number) => void;
	ptOnly?: boolean;
	usesPt?: boolean;
	onSkip?: () => void;
}

const PART_TYPE_COLORS: Record<string, string> = {
	oem: 'blue',
	'a/m': 'green',
	rcy: 'orange',
	'oe-surplus': 'purple',
	're-man': 'teal',
	're-con': 'cyan',
	other: 'gray',
};

export const SupplierMappingUI = ({
	ptSuppliers,
	opsSuppliers,
	existingSuppliers = [],
	onConfirm,
	rowMappings,
	onToggleRow,
	onAddPt,
	onRemovePt,
	onAddOps,
	onRemoveOps,
	ptOnly = false,
	usesPt = true,
	isConfirmLoading = false,
	onSkip,
}: SupplierMappingUIProps) => {
	const ptMap = new Map(ptSuppliers.map((supplier) => [supplier.id, supplier]));
	const opsMap = new Map(opsSuppliers.map((supplier) => [supplier.dealerId, supplier]));
	const existingMap = new Map(existingSuppliers.map((s) => [s._id, s]));

	const allAssignedPtIds = new Set(
		Object.values(rowMappings).flatMap((row) => (row.enabled ? row.ptIds : []))
	);
	const allAssignedOpsIds = new Set(
		Object.values(rowMappings).flatMap((row) => (row.enabled ? row.opsDealerIds : []))
	);

	const handleConfirm = () => {
		const flat: SupplierMapping[] = [];
		for (const row of Object.values(rowMappings)) {
			if (!row.enabled) continue;
			if (row.ptIds.length === 0 && row.opsDealerIds.length === 0) continue;

			if (row.ptIds.length > 0) {
				for (const ptId of row.ptIds) {
					flat.push({
						ptSupplierId: ptId,
						opsDealerIds: row.opsDealerIds.length > 0 ? row.opsDealerIds : undefined,
						existingId: row.existingId,
					});
				}
			} else {
				flat.push({
					opsDealerIds: row.opsDealerIds.length > 0 ? row.opsDealerIds : undefined,
					existingId: row.existingId,
				});
			}
		}
		onConfirm(flat);
	};

	const getRowPartTypes = (row: SupplierRowMapping): string[] => {
		const partTypes = new Set<string>();
		for (const ptId of row.ptIds) {
			ptMap.get(ptId)?.partTypes.forEach((type) => partTypes.add(type));
		}
		if (row.opsDealerIds.length > 0) partTypes.add('oem');
		return Array.from(partTypes);
	};

	type RowStatus = 'id-matched' | 'auto-merged' | 'new-matched' | 'unmapped' | 'disabled';

	const getRowStatus = (key: string, row: SupplierRowMapping): RowStatus => {
		if (!row.enabled) return 'disabled';
		if (row.existingId) {
			if (key.startsWith('existing-')) return 'id-matched';
			return 'auto-merged';
		}
		if (row.ptIds.length > 0 || row.opsDealerIds.length > 0) return 'new-matched';
		return 'unmapped';
	};

	const rowKeys = Object.keys(rowMappings).sort((a, b) => {
		const order: Record<RowStatus, number> = {
			'auto-merged': 0,
			'new-matched': 1,
			'unmapped': 2,
			'id-matched': 3,
			'disabled': 4,
		};
		const statusA = getRowStatus(a, rowMappings[a]);
		const statusB = getRowStatus(b, rowMappings[b]);
		const orderA = order[statusA] ?? 99;
		const orderB = order[statusB] ?? 99;
		if (orderA !== orderB) return orderA - orderB;
		return a.localeCompare(b);
	});

	const existingRows = rowKeys.filter((key) => !!rowMappings[key].existingId);
	const newRows = rowKeys.filter((key) => !rowMappings[key].existingId);
	const [showExisting, setShowExisting] = useState(false);

	const partBayStatusIcon = (tooltip: string) => (
		<Tooltip label={tooltip} placement="top" hasArrow>
			<Box
				w="18px"
				h="18px"
				borderRadius="full"
				bg="blue.50"
				border="1px solid"
				borderColor="blue.300"
				display="flex"
				alignItems="center"
				justifyContent="center"
			>
				<Image src={YaxleLogo} w="12px" h="12px" objectFit="contain" alt="" />
			</Box>
		</Tooltip>
	);

	const renderSelectableList = (
		label: string,
		options: Array<{ id: number; name: string }>,
		onSelect: (id: number) => void,
		maxWidth: string
	) => {
		if (options.length === 0) return null;
		return (
			<Menu>
				<MenuButton
					as={Button}
					size="xs"
					variant="outline"
					rightIcon={<Icon as={FaChevronDown as React.ElementType} boxSize={2} />}
					fontWeight="normal"
					color="gray.600"
					borderColor="gray.200"
					width="fit-content"
					maxW={maxWidth}
				>
					<Text noOfLines={1} fontSize="xs">{label}</Text>
				</MenuButton>
				<Portal>
					<MenuList maxH="200px" overflowY="auto" zIndex={1400} fontSize="sm">
						{options.map((option) => (
							<MenuItem key={option.id} onClick={() => onSelect(option.id)} fontSize="sm">
								{option.name}
							</MenuItem>
						))}
					</MenuList>
				</Portal>
			</Menu>
		);
	};

	const renderRow = (key: string, isLast: boolean) => {
		const row = rowMappings[key];
		const status = getRowStatus(key, row);
		const partTypes = getRowPartTypes(row);
		const existingTitle = row.existingId ? existingMap.get(row.existingId)?.title : undefined;
		const availablePt = ptSuppliers
			.filter((supplier) => row.ptIds.includes(supplier.id) || !allAssignedPtIds.has(supplier.id))
			.map((supplier) => ({ id: supplier.id, name: supplier.name }));
		const availableOps = opsSuppliers
			.filter((supplier) => row.opsDealerIds.includes(supplier.dealerId) || !allAssignedOpsIds.has(supplier.dealerId))
			.map((supplier) => ({ id: supplier.dealerId, name: supplier.name }));

		const CELL_DIVIDER = { borderLeftWidth: 1, borderColor: 'gray.300' } as const;
		const firstPtName = row.ptIds.length > 0 ? (ptMap.get(row.ptIds[0])?.name ?? `PT #${row.ptIds[0]}`) : null;
		const firstOpsName = row.opsDealerIds.length > 0 ? (opsMap.get(row.opsDealerIds[0])?.name ?? `OPS #${row.opsDealerIds[0]}`) : null;
		const primaryPartType = partTypes[0];

		return (
			<Flex
				key={key}
				pl={4}
				borderBottomWidth={isLast ? 0 : 1}
				borderColor="gray.200"
				align="stretch"
				bg={status === 'id-matched' ? 'blue.50' : status === 'auto-merged' ? 'green.50' : status === 'disabled' ? 'gray.50' : 'white'}
				opacity={status === 'disabled' ? 0.6 : 1}
				minH="48px"
			>
				<Flex width="28px" flexShrink={0} align="center" pr={2}>
					<Checkbox isChecked={row.enabled} onChange={() => onToggleRow(key)} colorScheme="blue" size="md" />
				</Flex>

				<Flex width="120px" flexShrink={0} justify="center" align="center" gap={1} px={2} {...CELL_DIVIDER}>
					{primaryPartType && (
						<Tooltip
							label={partTypes.map((t) => t.toUpperCase()).join(', ')}
							placement="top"
							hasArrow
							isDisabled={partTypes.length <= 1}
						>
							<HStack spacing={1} cursor={partTypes.length > 1 ? 'help' : 'default'}>
								<Badge colorScheme={PART_TYPE_COLORS[primaryPartType] ?? 'gray'} fontSize="2xs" px={1.5} borderRadius="full">
									{primaryPartType.toUpperCase()}
								</Badge>
								{partTypes.length > 1 && (
									<Text fontSize="2xs" color="gray.500" fontWeight="semibold">+{partTypes.length - 1}</Text>
								)}
							</HStack>
						</Tooltip>
					)}
				</Flex>

				{usesPt && (
					<Flex flex={1} minW={0} px={3} align="center" {...CELL_DIVIDER}>
						{row.enabled ? (
							<HStack spacing={1.5} overflow="hidden" width="100%">
								{firstPtName && (
									<Tooltip
										label={row.ptIds.length > 1 ? row.ptIds.map((id) => ptMap.get(id)?.name ?? `PT #${id}`).join(', ') : firstPtName}
										placement="top"
										hasArrow
									>
										<Tag size="sm" colorScheme="blue" borderRadius="full" variant="subtle" maxW="100%">
											<TagLabel fontSize="xs" noOfLines={1}>{firstPtName}</TagLabel>
											<TagCloseButton onClick={() => onRemovePt(key, row.ptIds[0])} />
										</Tag>
									</Tooltip>
								)}
								{row.ptIds.length > 1 && (
									<Tooltip
										label={row.ptIds.slice(1).map((id) => ptMap.get(id)?.name ?? `PT #${id}`).join(', ')}
										placement="top"
										hasArrow
									>
										<Badge colorScheme="blue" variant="outline" fontSize="2xs" borderRadius="full" cursor="help" flexShrink={0}>
											+{row.ptIds.length - 1}
										</Badge>
									</Tooltip>
								)}
								{renderSelectableList(
									row.ptIds.length === 0 ? 'Select PT supplier' : 'Add',
									availablePt.filter((option) => !row.ptIds.includes(option.id)),
									(id) => onAddPt(key, id),
									'160px'
								)}
							</HStack>
						) : (
							<Text fontSize="xs" color="gray.400" fontStyle="italic">--</Text>
						)}
					</Flex>
				)}

				{!ptOnly && (
					<Flex flex={1} minW={0} px={3} align="center" {...CELL_DIVIDER}>
						{row.enabled ? (
							<HStack spacing={1.5} overflow="hidden" width="100%">
								{firstOpsName && (
									<Tooltip
										label={row.opsDealerIds.length > 1 ? row.opsDealerIds.map((id) => opsMap.get(id)?.name ?? `OPS #${id}`).join(', ') : firstOpsName}
										placement="top"
										hasArrow
									>
										<Tag size="sm" colorScheme="green" borderRadius="full" variant="subtle" maxW="100%">
											<TagLabel fontSize="xs" noOfLines={1}>{firstOpsName}</TagLabel>
											<TagCloseButton onClick={() => onRemoveOps(key, row.opsDealerIds[0])} />
										</Tag>
									</Tooltip>
								)}
								{row.opsDealerIds.length > 1 && (
									<Tooltip
										label={row.opsDealerIds.slice(1).map((id) => opsMap.get(id)?.name ?? `OPS #${id}`).join(', ')}
										placement="top"
										hasArrow
									>
										<Badge colorScheme="green" variant="outline" fontSize="2xs" borderRadius="full" cursor="help" flexShrink={0}>
											+{row.opsDealerIds.length - 1}
										</Badge>
									</Tooltip>
								)}
								{renderSelectableList(
									row.opsDealerIds.length === 0 ? 'Select OPS supplier' : 'Add',
									availableOps.filter((option) => !row.opsDealerIds.includes(option.id)),
									(id) => onAddOps(key, id),
									'160px'
								)}
							</HStack>
						) : (
							<Text fontSize="xs" color="gray.400" fontStyle="italic">--</Text>
						)}
					</Flex>
				)}

				<Flex width="160px" flexShrink={0} justify="center" direction="column" align="center" gap={0.5} px={2} py={2} {...CELL_DIVIDER}>
					{status === 'id-matched' && partBayStatusIcon(brandCopy('Already linked in PartBay'))}
					{status === 'auto-merged' && (
						<Tooltip label="Auto-merged by ER scoring" placement="top" hasArrow>
							<Badge colorScheme="green" fontSize="2xs" borderRadius="full" cursor="default">Merged</Badge>
						</Tooltip>
					)}
					{status === 'new-matched' && (
						<Tooltip label="New supplier with marketplace links" placement="top" hasArrow>
							<Badge colorScheme="blue" fontSize="2xs" borderRadius="full" cursor="default">New</Badge>
						</Tooltip>
					)}
					{status === 'unmapped' && (
						<Tooltip label="Could not be matched automatically" placement="top" hasArrow>
							<Badge colorScheme="orange" fontSize="2xs" borderRadius="full" cursor="default">Unmapped</Badge>
						</Tooltip>
					)}
					{existingTitle && (
						<Tooltip label={existingTitle} placement="top" hasArrow>
							<Text fontSize="2xs" color="gray.500" noOfLines={1} maxW="150px" textAlign="center">
								→ {existingTitle}
							</Text>
						</Tooltip>
					)}
				</Flex>
			</Flex>
		);
	};

	return (
		<Flex direction="column" flex={1} width="100%" minH={0}>
			<Box flex={1} minH={0} overflowY="auto" pt={4}>
				<Box mb={4}>
					<Heading size="md" mb={1}>Supplier Network</Heading>
					<Text color="gray.500" fontSize="sm">
						Review discovered suppliers and their marketplace links before saving to your network.
					</Text>
				</Box>

				<Box borderWidth={1} borderColor="gray.200" borderRadius="md" overflow="hidden">
					<Flex
						px={4}
						py={2.5}
						bg="gray.50"
						borderBottomWidth={1}
						borderColor="gray.200"
						align="center"
						position="sticky"
						top={0}
						zIndex={2}
					>
						<Box width="28px" flexShrink={0} />
						<Text width="120px" flexShrink={0} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" textAlign="center" px={2}>
							Part Types
						</Text>
						{usesPt && (
							<Text flex={1} minW={0} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" px={3}>
								PT Supplier
							</Text>
						)}
						{!ptOnly && (
							<Text flex={1} minW={0} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" px={3}>
								OPS Supplier
							</Text>
						)}
						<Text width="160px" flexShrink={0} fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase" letterSpacing="wide" textAlign="center" px={2}>
							Status
						</Text>
					</Flex>

					{newRows.map((key, index) => renderRow(key, index === newRows.length - 1 && existingRows.length === 0))}

					{existingRows.length > 0 && (
						<>
							<Flex
								px={4}
								py={2}
								bg="gray.50"
								borderBottomWidth={1}
								borderTopWidth={newRows.length > 0 ? 1 : 0}
								borderColor="gray.200"
								align="center"
								cursor="pointer"
								onClick={() => setShowExisting((value) => !value)}
								_hover={{ bg: 'gray.100' }}
								transition="background 0.15s"
								userSelect="none"
							>
								<Icon as={(showExisting ? FaChevronDown : FaChevronRight) as React.ElementType} boxSize={2.5} color="gray.500" mr={2} />
								<Text fontSize="xs" fontWeight="semibold" color="gray.500">
									Existing in our system ({existingRows.length})
								</Text>
							</Flex>
							{showExisting && existingRows.map((key, index) => renderRow(key, index === existingRows.length - 1))}
						</>
					)}
				</Box>
			</Box>

			<StepFooter
				secondaryActions={onSkip
					? [{ label: 'Skip', onClick: onSkip, variant: 'outline' }]
					: undefined}
				primary={{
					label: 'Confirm Supplier Network',
					onClick: handleConfirm,
					isLoading: isConfirmLoading,
					loadingText: 'Saving...',
				}}
			/>
		</Flex>
	);
};
